import React, { useState, useEffect, useMemo } from 'react';

import axiosInstance from '../../config/axiosConfig';
import { formatMoney } from '../../utils/utils';
/* global gdAlertify */

const CartolaPacienteModal = ({ show, handleClose, paciente }) => {
    const [detalle, setDetalle] = useState([]);
    const [loading, setLoading] = useState(false);
    
    // Form for new manual cargo
    const [showFormCargo, setShowFormCargo] = useState(false);
    const [nuevoCargo, setNuevoCargo] = useState({
        monto: '',
        fecha: new Date().toISOString().split('T')[0],
        observacion: ''
    });

    const [showFormAbono, setShowFormAbono] = useState(false);
    const [nuevoAbono, setNuevoAbono] = useState({
        monto: '',
        fecha: new Date().toISOString().split('T')[0],
        metodo: 'Transferencia',
        observacion: ''
    });

    const [filtros, setFiltros] = useState({
        desde: '',
        hasta: ''
    });
    const [movimientoEditando, setMovimientoEditando] = useState(null);
    const [movimientoForm, setMovimientoForm] = useState({
        monto: '',
        observacion: ''
    });

    const resumenInterno = useMemo(() => {
        // Calculate totals from the UN-reversed or reversed list, doesn't matter for sum.
        return (detalle || []).reduce((acc, row) => ({
            totalCargos: acc.totalCargos + parseInt(row.monto_cargo || 0),
            totalAbonos: acc.totalAbonos + parseInt(row.monto_abono || 0)
        }), { totalCargos: 0, totalAbonos: 0 });
    }, [detalle]);

    useEffect(() => {
        if (show && paciente) {
            fetchDetalleCartola();
            // Reset forms whenever modal opens
            setShowFormCargo(false);
            setShowFormAbono(false);
            setMovimientoEditando(null);
        }
    }, [show, paciente]);

    const fetchDetalleCartola = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get(`/acuerdo-mutuo/cartola-pacientes/detalle/${paciente.paciente_id}`);
            
            // Calculate running balance but ascending first to keep math right
            let currentBalance = 0;
            const detCalc = (response.data.data || []).map(row => {
                const montoCargo = parseInt(row.monto_cargo || 0);
                const montoAbono = parseInt(row.monto_abono || 0);
                currentBalance = currentBalance + montoCargo - montoAbono;
                return {
                    ...row,
                    saldo_acumulado: currentBalance
                };
            });
            
            // To show newest first, we reverse the array BEFORE setting the state
            setDetalle(detCalc.reverse());
        } catch (error) {
            console.error(error);
            gdAlertify.notiError("Error al cargar el detalle de la cartola.");
        } finally {
            setLoading(false);
        }
    };

    const handleGuardarCargo = async () => {
        if (!nuevoCargo.monto || nuevoCargo.monto <= 0) {
            gdAlertify.notiError("El monto debe ser mayor a 0.");
            return;
        }

        gdAlertify.showLoader();
        try {
            await axiosInstance.post('/acuerdo-mutuo/cartola-pacientes/cargo-manual', {
                paciente_id: paciente.paciente_id,
                monto: nuevoCargo.monto,
                fecha: nuevoCargo.fecha,
                observacion: nuevoCargo.observacion
            });
            
            gdAlertify.notiSuccess("Cargo manual agregado correctamente.");
            setShowFormCargo(false);
            setNuevoCargo({ monto: '', fecha: new Date().toISOString().split('T')[0], observacion: '' });
            fetchDetalleCartola(); // Refresh details
            
            // We should ideally tell the parent list to refresh too.
        } catch (error) {
            console.error(error);
            gdAlertify.notiError("Error al guardar el cargo manual.");
        } finally {
            gdAlertify.hideLoader();
        }
    };

    const handleGuardarAbono = async () => {
        if (!nuevoAbono.monto || nuevoAbono.monto <= 0) {
            gdAlertify.notiError("El monto debe ser mayor a 0.");
            return;
        }

        gdAlertify.showLoader();
        try {
            await axiosInstance.post('/acuerdo-mutuo/cartola-pacientes/registrar-abono', {
                paciente_id: paciente.paciente_id,
                monto: nuevoAbono.monto,
                fecha: nuevoAbono.fecha,
                metodo: nuevoAbono.metodo,
                observacion: nuevoAbono.observacion
            });
            
            gdAlertify.notiSuccess("Abono registrado y distribuido correctamente.");
            setShowFormAbono(false);
            setNuevoAbono({ monto: '', fecha: new Date().toISOString().split('T')[0], metodo: 'Transferencia', observacion: '' });
            fetchDetalleCartola(); // Refresh details
            
        } catch (error) {
            console.error(error);
            gdAlertify.notiError("Error al registrar el abono.");
        } finally {
            gdAlertify.hideLoader();
        }
    };

    const handleEliminarAbono = async (idAbono) => {
        gdAlertify.confirm({
            title: "¿Deshacer Abono?",
            message: "Se eliminará este abono y el saldo pendiente del paciente volverá a ser cargado con esta cantidad. ¿Estás seguro?",
            onConfirm: async () => {
                gdAlertify.showLoader();
                try {
                    await axiosInstance.post('/acuerdo-mutuo/cobranza/pago/anular', { id_pago: idAbono });
                    gdAlertify.notiSuccess("Abono anulado con éxito.");
                    fetchDetalleCartola(); // Refresh the modal
                } catch (error) {
                    console.error(error);
                    gdAlertify.notiError("Error al anular el abono.");
                } finally {
                    gdAlertify.hideLoader();
                }
            },
            onCancel: () => {}
        });
    };

    const handleEliminarCargo = async (idCargo) => {
        gdAlertify.confirm({
            title: "¿Eliminar Cargo Manual?",
            message: "Se eliminará este cargo extra y el saldo pendiente del paciente disminuirá. ¿Estás seguro?",
            onConfirm: async () => {
                gdAlertify.showLoader();
                try {
                    await axiosInstance.post('/acuerdo-mutuo/cartola-pacientes/cargo-manual/anular', { id_cargo: idCargo });
                    gdAlertify.notiSuccess("Cargo manual eliminado con éxito.");
                    fetchDetalleCartola(); // Refresh the modal
                } catch (error) {
                    console.error(error);
                    gdAlertify.notiError("Error al eliminar el cargo.");
                } finally {
                    gdAlertify.hideLoader();
                }
            },
            onCancel: () => {}
        });
    };

    const getMontoMovimiento = (row) => {
        return Number(row.monto_abono || 0) > 0 ? row.monto_abono : row.monto_cargo;
    };

    const getReferenciaLimpia = (referencia) => {
        return (referencia || '')
            .replace(/\b\d{4}-\d{2}-\d{2}\b/g, '')
            .replace(/\b\d{2}-\d{2}-\d{4}\b/g, '')
            .replace(/\s{2,}/g, ' ')
            .replace(/\s*[-|/]\s*$/, '')
            .replace(/^[-|/]\s*/, '')
            .trim();
    };

    const getObservacionLimpia = (observacion) => {
        return (observacion || '').replace(/\s*\{F:[^}]+\}/g, '').trim();
    };

    const handleEditarMovimiento = (row) => {
        setMovimientoEditando(`${row.tipo}-${row.id}`);
        setMovimientoForm({
            monto: String(getMontoMovimiento(row) || ''),
            observacion: getObservacionLimpia(row.observacion)
        });
    };

    const handleGuardarMovimiento = async (row) => {
        const monto = Number(movimientoForm.monto || 0);

        if (!row.id) {
            gdAlertify.notiError("No se puede editar este movimiento porque no tiene identificador.");
            return;
        }

        if (!monto || monto <= 0) {
            gdAlertify.notiError("El monto debe ser mayor a 0.");
            return;
        }

        gdAlertify.showLoader();
        try {
            await axiosInstance.post('/acuerdo-mutuo/cartola-pacientes/movimiento/editar', {
                id: row.id,
                tipo: row.tipo,
                paciente_id: paciente.paciente_id,
                monto,
                monto_cargo: Number(row.monto_cargo || 0) > 0 ? monto : 0,
                monto_abono: Number(row.monto_abono || 0) > 0 ? monto : 0,
                observacion: movimientoForm.observacion,
                motivo_log: `Edicion movimiento cartola paciente ${paciente.paciente_id}: ${row.tipo} #${row.id}`
            });

            gdAlertify.notiSuccess("Movimiento actualizado correctamente.");
            setMovimientoEditando(null);
            fetchDetalleCartola();
        } catch (error) {
            console.error(error);
            gdAlertify.notiError("Error al actualizar el movimiento.");
        } finally {
            gdAlertify.hideLoader();
        }
    };

    const detalleFiltrado = detalle.filter(row => {
        if (!row.fecha) return true;
        const fechaRow = row.fecha.split(' ')[0];
        if (filtros.desde && fechaRow < filtros.desde) return false;
        if (filtros.hasta && fechaRow > filtros.hasta) return false;
        return true;
    });

    if (!show || !paciente) return null;

    const pacienteNombreCompleto = [
        paciente.nombres,
        paciente.apellidoPaterno,
        paciente.apellidoMaterno
    ].filter(Boolean).join(' ');

    return (
        <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)", overflowY: "auto", zIndex: 1050 }}>
            <div className="modal-dialog modal-xl modal-dialog-centered">
                <div className="modal-content border-0 shadow-lg">
                    <div className="modal-header bg-light">
                        <h5 className="modal-title">
                            <i className="bi bi-person-lines-fill text-primary me-2"></i>
                            Cartola Detallada: <span className="fw-bold">{pacienteNombreCompleto}</span>
                        </h5>
                        <button type="button" className="btn-close" onClick={handleClose}></button>
                    </div>

                    <div className="modal-body p-0 bg-white">
                        <div className="bg-white p-3 border-bottom">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h6 className="text-muted mb-0 fw-bold text-uppercase"><i className="bi bi-graph-up me-2"></i>Resumen Financiero del Paciente</h6>
                                <div>
                                    <button 
                                        className="btn btn-success shadow-sm fw-bold btn-sm rounded-pill px-3 me-2"
                                        onClick={() => {
                                            setShowFormAbono(!showFormAbono);
                                            setShowFormCargo(false);
                                        }}
                                    >
                                        <i className={`bi ${showFormAbono ? 'bi-x-circle' : 'bi-plus-circle'} me-1`}></i>
                                        {showFormAbono ? 'Cerrar' : 'Registrar Abono'}
                                    </button>
                                    <button 
                                        className="btn btn-warning shadow-sm fw-bold btn-sm rounded-pill px-3"
                                        onClick={() => {
                                            setShowFormCargo(!showFormCargo);
                                            setShowFormAbono(false);
                                        }}
                                    >
                                        <i className={`bi ${showFormCargo ? 'bi-x-circle' : 'bi-plus-circle'} me-1`}></i>
                                        {showFormCargo ? 'Cerrar' : 'Agregar Cargo'}
                                    </button>
                                </div>
                            </div>

                            {/* Info de Tutor y Notificaciones */}
                            <div className="bg-light p-3 rounded-2 mb-3 border">
                                <div className="row">
                                    <div className="col-md-4 border-end">
                                        <small className="text-muted d-block fw-bold mb-1"><i className="bi bi-person-badge me-1"></i>Tutor de Pago:</small>
                                        <div className="fw-bold">{paciente.tutor_nombre || '—'}</div>
                                    </div>
                                    <div className="col-md-4 border-end">
                                        <small className="text-muted d-block fw-bold mb-1"><i className="bi bi-envelope me-1"></i>Email Tutor:</small>
                                        <div className="fw-bold text-primary">{paciente.tutor_correo || '—'}</div>
                                    </div>
                                    <div className="col-md-4">
                                        <small className="text-muted d-block fw-bold mb-1"><i className="bi bi-envelope-at me-1"></i>Emails Notificaciones:</small>
                                        <div className="fw-bold text-secondary" style={{fontSize: '0.85rem'}}>{paciente.correos_notificacion || '—'}</div>
                                    </div>
                                </div>
                            </div>
                            <div className="row g-3">
                                <div className="col-md-4">
                                    <div className="card shadow-sm border-0 bg-light border-start border-danger border-4 h-100">
                                        <div className="card-body py-2 d-flex align-items-center justify-content-between">
                                            <div>
                                                <h6 className="text-muted fw-bold mb-1" style={{fontSize: '0.8rem'}}>Total Cargos (Histórico)</h6>
                                                <h5 className="mb-0 text-dark fw-bold">
                                                    ${formatMoney(resumenInterno.totalCargos)}
                                                </h5>
                                            </div>
                                            <div className="text-danger opacity-75">
                                                <i className="bi bi-receipt fs-3"></i>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="card shadow-sm border-0 bg-light border-start border-success border-4 h-100">
                                        <div className="card-body py-2 d-flex align-items-center justify-content-between">
                                            <div>
                                                <h6 className="text-muted fw-bold mb-1" style={{fontSize: '0.8rem'}}>Total Abonos Recibidos</h6>
                                                <h5 className="mb-0 text-success fw-bold">
                                                    ${formatMoney(resumenInterno.totalAbonos)}
                                                </h5>
                                            </div>
                                            <div className="text-success opacity-75">
                                                <i className="bi bi-cash-stack fs-3"></i>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="card shadow-sm border-0 bg-light border-start border-primary border-4 h-100">
                                        <div className="card-body py-2 d-flex align-items-center justify-content-between">
                                            <div>
                                                <h6 className="text-muted fw-bold mb-1" style={{fontSize: '0.8rem'}}>Saldo Pendiente Modal</h6>
                                                <h5 className={`mb-0 fw-bold ${(resumenInterno.totalCargos - resumenInterno.totalAbonos) > 0 ? 'text-danger' : 'text-primary'}`}>
                                                    ${formatMoney(resumenInterno.totalCargos - resumenInterno.totalAbonos)}
                                                </h5>
                                            </div>
                                            <div className="text-primary opacity-75">
                                                <i className="bi bi-piggy-bank fs-3"></i>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Formulario de Cargo Manual (Desplegable) */}
                        {showFormCargo && (
                            <div className="bg-light border-bottom p-3 shadow-inner">
                                <h6 className="fw-bold text-dark mb-3"><i className="bi bi-receipt me-2"></i>Nuevo Cargo Manual</h6>
                                <div className="row g-2 align-items-end">
                                    <div className="col-md-3">
                                        <label className="form-label small fw-bold">Fecha</label>
                                        <input 
                                            type="date" 
                                            className="form-control" 
                                            value={nuevoCargo.fecha}
                                            onChange={(e) => setNuevoCargo({...nuevoCargo, fecha: e.target.value})}
                                        />
                                    </div>
                                    <div className="col-md-3">
                                        <label className="form-label small fw-bold">Monto ($)</label>
                                        <input 
                                            type="text" 
                                            className="form-control fs-4 fw-bold text-end" 
                                            placeholder="0"
                                            value={nuevoCargo.monto ? formatMoney(nuevoCargo.monto) : ''}
                                            onChange={(e) => {
                                                const val = e.target.value.replace(/\D/g, "");
                                                setNuevoCargo({...nuevoCargo, monto: val});
                                            }}
                                        />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label small fw-bold">Observación / Motivo</label>
                                        <input 
                                            type="text" 
                                            className="form-control" 
                                            placeholder="Ej: Insumos extra"
                                            value={nuevoCargo.observacion}
                                            onChange={(e) => setNuevoCargo({...nuevoCargo, observacion: e.target.value})}
                                        />
                                    </div>
                                    <div className="col-md-2 d-grid">
                                        <button className="btn btn-success fw-bold" onClick={handleGuardarCargo}>
                                            Guardar
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Formulario de Abono Manual (Desplegable) */}
                        {showFormAbono && (
                            <div className="bg-light border-bottom p-3 shadow-inner border-success border-start border-4">
                                <h6 className="fw-bold text-success mb-3"><i className="bi bi-cash-stack me-2"></i>Registrar Nuevo Abono</h6>
                                <div className="row g-2 align-items-end">
                                    <div className="col-md-2">
                                        <label className="form-label small fw-bold">Fecha</label>
                                        <input 
                                            type="date" 
                                            className="form-control" 
                                            value={nuevoAbono.fecha}
                                            onChange={(e) => setNuevoAbono({...nuevoAbono, fecha: e.target.value})}
                                        />
                                    </div>
                                    <div className="col-md-2">
                                        <label className="form-label small fw-bold">Monto ($)</label>
                                        <input 
                                            type="text" 
                                            className="form-control fs-4 fw-bold text-end" 
                                            placeholder="0"
                                            value={nuevoAbono.monto ? formatMoney(nuevoAbono.monto) : ''}
                                            onChange={(e) => {
                                                const val = e.target.value.replace(/\D/g, "");
                                                setNuevoAbono({...nuevoAbono, monto: val});
                                            }}
                                        />
                                    </div>
                                    <div className="col-md-3">
                                        <label className="form-label small fw-bold">Método</label>
                                        <select 
                                            className="form-select" 
                                            value={nuevoAbono.metodo}
                                            onChange={(e) => setNuevoAbono({...nuevoAbono, metodo: e.target.value})}
                                        >
                                            <option value="Transferencia">Transferencia</option>
                                            <option value="Efectivo">Efectivo</option>
                                            <option value="Tarjeta">Tarjeta Déb/Crédito</option>
                                        </select>
                                    </div>
                                    <div className="col-md-3">
                                        <label className="form-label small fw-bold">Observación / Referencia</label>
                                        <input 
                                            type="text" 
                                            className="form-control" 
                                            placeholder="Ej: Transf. Bci"
                                            value={nuevoAbono.observacion}
                                            onChange={(e) => setNuevoAbono({...nuevoAbono, observacion: e.target.value})}
                                        />
                                    </div>
                                    <div className="col-md-2 d-grid">
                                        <button className="btn btn-success fw-bold" onClick={handleGuardarAbono}>
                                            <i className="bi bi-check-circle me-1"></i> Confirmar
                                        </button>
                                    </div>
                                    <div className="col-12 mt-2">
                                        <small className="text-muted"><i className="bi bi-info-circle me-1"></i>El abono saldará automáticamente la deuda de los meses más antiguos primero (Método FIFO).</small>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="p-3 bg-light border-bottom d-flex align-items-center gap-3">
                            <div className="fw-bold text-muted small text-uppercase"><i className="bi bi-funnel-fill me-1"></i>Filtrar Movimientos:</div>
                            <div className="d-flex align-items-center gap-2">
                                <label className="small mb-0">Desde:</label>
                                <input type="date" className="form-control form-control-sm" value={filtros.desde} onChange={e => setFiltros({...filtros, desde: e.target.value})} />
                            </div>
                            <div className="d-flex align-items-center gap-2">
                                <label className="small mb-0">Hasta:</label>
                                <input type="date" className="form-control form-control-sm" value={filtros.hasta} onChange={e => setFiltros({...filtros, hasta: e.target.value})} />
                            </div>
                            <button className="btn btn-sm btn-outline-secondary" onClick={() => setFiltros({desde: '', hasta: ''})}>Limpiar</button>
                        </div>

                        <div className="p-3">
                            {loading ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Cargando...</span>
                                    </div>
                                </div>
                            ) : detalleFiltrado.length === 0 ? (
                                <div className="text-center py-5 text-muted">
                                    <i className="bi bi-inbox fs-1 d-block mb-2"></i>
                                    No hay movimientos que coincidan con el filtro.
                                </div>
                            ) : (
                                <div className="table-responsive" style={{ maxHeight: '500px' }}>
                                    <table className="table table-hover table-bordered mb-0 table-sm">
                                        <thead className="table-light position-sticky top-0 bg-light" style={{ zIndex: 1 }}>
                                            <tr>
                                                <th className="text-center" width="10%">Fecha</th>
                                                <th width="15%">Tipo</th>
                                                <th width="20%">Referencia</th>
                                                <th width="25%">Observación</th>
                                                <th className="text-end" width="10%">Cargos</th>
                                                <th className="text-end" width="10%">Abonos</th>
                                                <th className="text-end" width="10%">Saldo</th>
                                                <th className="text-center" width="5%"></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {detalleFiltrado.map((row, idx) => {
                                                let badgeColor = "bg-secondary";
                                                let icon = "";
                                                const editKey = `${row.tipo}-${row.id}`;
                                                const isEditing = movimientoEditando === editKey;

                                                if (row.tipo === 'liquidacion') {
                                                    badgeColor = "bg-primary";
                                                    icon = "bi-calendar-check";
                                                } else if (row.tipo === 'cargo_manual') {
                                                    badgeColor = "bg-warning text-dark";
                                                    icon = "bi-exclamation-circle";
                                                } else if (row.tipo === 'pago') {
                                                    badgeColor = "bg-success";
                                                    icon = "bi-cash-coin";
                                                }

                                                return (
                                                    <tr key={idx}>
                                                        <td className="text-center align-middle">{row.fecha ? row.fecha.split(' ')[0].split('-').reverse().join('-') : ''}</td>
                                                        <td className="align-middle">
                                                            <span className={`badge ${badgeColor} d-flex align-items-center w-fit`}>
                                                                <i className={`bi ${icon} me-1`}></i>
                                                                {row.tipo.replace('_', ' ').toUpperCase()}
                                                            </span>
                                                            {(row.tipo === 'liquidacion' || row.tipo === 'cargo_manual') && Number(row.saldo_pendiente) > 0 && (
                                                                <span className="badge bg-danger mt-1 d-block w-fit">DEBE</span>
                                                            )}
                                                        </td>
                                                        <td className="align-middle fw-bold small text-dark">{getReferenciaLimpia(row.referencia) || '-'}</td>
                                                        <td className="align-middle small text-muted fst-italic">
                                                            {isEditing ? (
                                                                <input
                                                                    type="text"
                                                                    className="form-control form-control-sm"
                                                                    value={movimientoForm.observacion}
                                                                    onChange={(e) => setMovimientoForm({...movimientoForm, observacion: e.target.value})}
                                                                />
                                                            ) : (
                                                                getObservacionLimpia(row.observacion) || '-'
                                                            )}
                                                        </td>
                                                        <td className="text-end align-middle fw-bold text-danger">
                                                            {isEditing && row.monto_cargo > 0 ? (
                                                                <input
                                                                    type="text"
                                                                    className="form-control form-control-sm text-end fw-bold"
                                                                    value={movimientoForm.monto ? formatMoney(movimientoForm.monto) : ''}
                                                                    onChange={(e) => setMovimientoForm({...movimientoForm, monto: e.target.value.replace(/\D/g, "")})}
                                                                />
                                                            ) : (
                                                                row.monto_cargo > 0 ? `$${formatMoney(row.monto_cargo)}` : ''
                                                            )}
                                                        </td>
                                                        <td className="text-end align-middle fw-bold text-success">
                                                            {isEditing && row.monto_abono > 0 ? (
                                                                <input
                                                                    type="text"
                                                                    className="form-control form-control-sm text-end fw-bold"
                                                                    value={movimientoForm.monto ? formatMoney(movimientoForm.monto) : ''}
                                                                    onChange={(e) => setMovimientoForm({...movimientoForm, monto: e.target.value.replace(/\D/g, "")})}
                                                                />
                                                            ) : (
                                                                row.monto_abono > 0 ? `$${formatMoney(row.monto_abono)}` : ''
                                                            )}
                                                        </td>
                                                        <td className="text-end align-middle fw-bold" style={{ backgroundColor: '#f8f9fa' }}>
                                                            ${formatMoney(row.saldo_acumulado || 0)}
                                                        </td>
                                                        <td className="text-center align-middle">
                                                            {isEditing ? (
                                                                <>
                                                                    <button
                                                                        className="btn btn-sm btn-outline-success border-0"
                                                                        onClick={() => handleGuardarMovimiento(row)}
                                                                        title="Guardar Movimiento"
                                                                    >
                                                                        <i className="bi bi-check-lg"></i>
                                                                    </button>
                                                                    <button
                                                                        className="btn btn-sm btn-outline-secondary border-0"
                                                                        onClick={() => setMovimientoEditando(null)}
                                                                        title="Cancelar"
                                                                    >
                                                                        <i className="bi bi-x-lg"></i>
                                                                    </button>
                                                                </>
                                                            ) : (
                                                                row.id && (
                                                                    <button
                                                                        className="btn btn-sm btn-outline-primary border-0"
                                                                        onClick={() => handleEditarMovimiento(row)}
                                                                        title="Editar Movimiento"
                                                                    >
                                                                        <i className="bi bi-pencil"></i>
                                                                    </button>
                                                                )
                                                            )}
                                                            {!isEditing && row.tipo === 'pago' && row.id && (
                                                                <button
                                                                    className="btn btn-sm btn-outline-danger border-0"
                                                                    onClick={() => handleEliminarAbono(row.id)}
                                                                    title="Eliminar Abono"
                                                                >
                                                                    <i className="bi bi-trash"></i>
                                                                </button>
                                                            )}
                                                            {!isEditing && row.tipo === 'cargo_manual' && row.id && (
                                                                <button
                                                                    className="btn btn-sm btn-outline-danger border-0"
                                                                    onClick={() => handleEliminarCargo(row.id)}
                                                                    title="Eliminar Cargo Manual"
                                                                >
                                                                    <i className="bi bi-trash"></i>
                                                                </button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="modal-footer bg-light border-top">
                        <button type="button" className="btn btn-secondary px-4 fw-bold shadow-sm" onClick={handleClose}>
                            Cerrar
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CartolaPacienteModal;
